const HEYTAOS_SEARCH_DEADLINE_MS = 5000;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matchCount = previousTerm ? currentButtonText.split(previousTerm).length - 1 : 0;
      if (previousTerm && matchCount > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex || !allVisibleElements.length) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getExploreUrl(searchTerm) {
    const exploreUrl = new URL(this.input.form.action, window.location.origin);
    exploreUrl.searchParams.set('q', searchTerm);
    return exploreUrl;
  }

  renderExploreSearchAction(searchTerm, message = '') {
    const exploreUrl = this.getExploreUrl(searchTerm);

    const results = document.createElement('div');
    results.id = 'predictive-search-results';
    results.setAttribute('role', 'listbox');

    const groups = document.createElement('div');
    groups.id = 'predictive-search-results-groups-wrapper';
    groups.className =
      'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';

    const group = document.createElement('div');
    group.className = 'predictive-search__result-group';

    const heading = document.createElement('h2');
    heading.id = 'predictive-search-explore-heading';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = 'HeyTaos search';

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-explore-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', heading.id);

    const option = document.createElement('li');
    option.id = 'predictive-search-option-explore';
    option.className = 'predictive-search__list-item';
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');

    const link = document.createElement('a');
    link.href = exploreUrl.toString();
    link.className = 'predictive-search__item link link--text';
    link.tabIndex = -1;

    const content = document.createElement('div');
    content.className = 'predictive-search__item-content predictive-search__item-content--centered';

    const label = document.createElement('p');
    label.className = 'predictive-search__item-heading predictive-search__item-query-result h5';
    label.setAttribute('data-predictive-search-search-for-text', '');
    label.textContent = message || `Search all HeyTaos guides for “${searchTerm}”`;

    content.appendChild(label);
    link.appendChild(content);
    option.appendChild(link);
    list.appendChild(option);
    group.appendChild(heading);
    group.appendChild(list);
    groups.appendChild(group);
    results.appendChild(groups);

    const liveCount = document.createElement('span');
    liveCount.className = 'visually-hidden';
    liveCount.setAttribute('data-predictive-search-live-region-count-value', '');
    liveCount.textContent = '1 search option available.';
    results.appendChild(liveCount);

    this.renderSearchResults(results.outerHTML);
  }

  safeInternalUrl(value) {
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
    return value;
  }

  renderHeyTaosApiResults(payload, searchTerm) {
    const apiResults = Array.isArray(payload?.results) ? payload.results.slice(0, 8) : [];

    if (!apiResults.length) {
      this.renderExploreSearchAction(searchTerm, `No direct matches. Search all HeyTaos guides for “${searchTerm}”`);
      return;
    }

    const results = document.createElement('div');
    results.id = 'predictive-search-results';
    results.setAttribute('role', 'listbox');

    const groups = document.createElement('div');
    groups.id = 'predictive-search-results-groups-wrapper';
    groups.className =
      'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';

    const group = document.createElement('div');
    group.className = 'predictive-search__result-group';

    const heading = document.createElement('h2');
    heading.id = 'predictive-search-heytaos-heading';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = 'HeyTaos results';

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-heytaos-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', heading.id);

    apiResults.forEach((item, index) => {
      const href = this.safeInternalUrl(item?.url);
      if (!href || !item?.title) return;

      const option = document.createElement('li');
      option.id = `predictive-search-option-heytaos-${index + 1}`;
      option.className = 'predictive-search__list-item';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = href;
      link.className = 'predictive-search__item link link--text';
      link.tabIndex = -1;

      const content = document.createElement('div');
      content.className = 'predictive-search__item-content predictive-search__item-content--centered';

      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = item.title;
      content.appendChild(title);

      const metaParts = [item.subcategory, item.category, item.town].filter(Boolean);
      if (metaParts.length) {
        const meta = document.createElement('p');
        meta.className = 'caption-with-letter-spacing';
        meta.textContent = metaParts.join(' · ');
        content.appendChild(meta);
      }

      if (item.summary) {
        const summary = document.createElement('p');
        summary.className = 'caption';
        summary.textContent = item.summary;
        content.appendChild(summary);
      }

      link.appendChild(content);
      option.appendChild(link);
      list.appendChild(option);
    });

    const viewAllOption = document.createElement('li');
    viewAllOption.id = 'predictive-search-option-heytaos-all';
    viewAllOption.className = 'predictive-search__list-item';
    viewAllOption.setAttribute('role', 'option');
    viewAllOption.setAttribute('aria-selected', 'false');

    const viewAllLink = document.createElement('a');
    viewAllLink.href = this.getExploreUrl(searchTerm).toString();
    viewAllLink.className = 'predictive-search__item link link--text';
    viewAllLink.tabIndex = -1;

    const viewAllContent = document.createElement('div');
    viewAllContent.className = 'predictive-search__item-content predictive-search__item-content--centered';

    const viewAllLabel = document.createElement('p');
    viewAllLabel.className = 'predictive-search__item-heading predictive-search__item-query-result h5';
    viewAllLabel.setAttribute('data-predictive-search-search-for-text', '');
    viewAllLabel.textContent = `View all results for “${searchTerm}”`;

    viewAllContent.appendChild(viewAllLabel);
    viewAllLink.appendChild(viewAllContent);
    viewAllOption.appendChild(viewAllLink);

    group.appendChild(heading);
    group.appendChild(list);
    group.appendChild(viewAllOption);
    groups.appendChild(group);
    results.appendChild(groups);

    const liveCount = document.createElement('span');
    liveCount.className = 'visually-hidden';
    liveCount.setAttribute('data-predictive-search-live-region-count-value', '');
    const resultCount = list.children.length;
    liveCount.textContent = `${resultCount} HeyTaos ${resultCount === 1 ? 'result' : 'results'} available.`;
    results.appendChild(liveCount);

    this.renderSearchResults(results.outerHTML);
  }

  getHeyTaosApiResults(searchTerm) {
    const queryKey = `heytaos-${searchTerm.replace(/\s+/g, '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const requestController = this.abortController;
    let timedOut = false;
    const deadlineTimer = window.setTimeout(() => {
      timedOut = true;
      requestController.abort();
    }, HEYTAOS_SEARCH_DEADLINE_MS);

    fetch(`https://heytaos-site-search.vlad-a21.workers.dev/v1/search?q=${encodeURIComponent(searchTerm)}`, {
      signal: requestController.signal,
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HeyTaos search API returned ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (this.getQuery() !== searchTerm) return;
        if (!payload || typeof payload !== 'object' || !Array.isArray(payload.results)) {
          throw new Error('HeyTaos search API returned an invalid payload');
        }

        this.renderHeyTaosApiResults(payload, searchTerm);
        const markup = this.predictiveSearchResults.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = markup;
        });
      })
      .catch((error) => {
        if (this.getQuery() !== searchTerm) return;
        if (error?.name === 'AbortError' && !timedOut) return;
        console.error('HeyTaos predictive search API failed:', error);
        this.renderExploreSearchAction(
          searchTerm,
          timedOut
            ? `Search took too long. Search all HeyTaos guides for “${searchTerm}”`
            : `Search is temporarily unavailable. Search all HeyTaos guides for “${searchTerm}”`
        );
      })
      .finally(() => window.clearTimeout(deadlineTimer));
  }

  getSearchResults(searchTerm) {
    if (this.input.form.hasAttribute('data-explore-search')) {
      this.getHeyTaosApiResults(searchTerm);
      return;
    }

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(liveRegion?.textContent || 'Search results available.');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
